import { Badge } from "@/components/ui/badge.jsx";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@/components/ui/table.jsx";
import {
  ASSET_METADATA_FIELDS,
  getAssetMetadataPlainText,
} from "@/lib/asset-metadata-fields.js";
import { formatTimestamp } from "@/lib/datetime-utils.js";
import { getStatusDisplayText } from "@/lib/formatting-utils.js";
import {
  getStatusBadgeClass,
  getStatusDotClass,
} from "@/lib/status-utils.js";

/**
 * Renders a formatted value cell for a metadata field.
 * @param {Object} asset
 * @param {{ key: string, format: string }} field
 * @returns {React.ReactNode}
 */
function MetadataValueCell({ asset, field }) {
  const value = asset?.[field.key];

  switch (field.format) {
    case "status":
      return (
        <Badge variant='status' className={getStatusBadgeClass(value)}>
          <div
            className={getStatusDotClass(
              value,
              "w-1.5 h-1.5 rounded-full"
            )}
          />
          {getStatusDisplayText(value)}
        </Badge>
      );
    case "optimization":
      return (
        <Badge
          variant='secondary'
          className='text-xs font-medium bg-muted text-foreground border border-border'>
          {value ? "Yes" : "No"}
        </Badge>
      );
    case "timestamp":
      return value ? formatTimestamp(value) : "-";
    case "coordinate":
      return (
        <span className='font-mono text-sm'>
          {value != null ? value : "-"}
        </span>
      );
    case "numberOptional":
      return value != null ? value : "-";
    case "textOptional":
      return value || "-";
    default:
      return getAssetMetadataPlainText(asset, field);
  }
}

/**
 * Condensed two-column metadata table for a single asset.
 * @param {{ asset: Object }} props
 * @returns {JSX.Element}
 */
export default function AssetMetadataSummary({ asset }) {
  return (
    <Table>
      <TableBody>
        {ASSET_METADATA_FIELDS.map((field) => (
          <TableRow
            key={field.key}
            className='border-b border-border hover:bg-transparent'>
            <TableCell className='w-44 py-2 px-3 text-sm font-medium text-muted-foreground align-top'>
              {field.label}
            </TableCell>
            <TableCell className='py-2 px-3 text-sm font-light text-foreground align-top'>
              <MetadataValueCell asset={asset} field={field} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
